import React, { useState } from 'react';

interface PhoneInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}

const PhoneInput: React.FC<PhoneInputProps> = ({ label = 'Telefone', value, onChange, required = false }) => {
  const [error, setError] = useState<string>('');

  const formatPhone = (input: string): string => {
    // Remove tudo que não é número
    const numbers = input.replace(/\D/g, '');
    
    // Formato brasileiro: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
    // Aceita 10 ou 11 dígitos (com ou sem 9º dígito)
    if (numbers.length <= 2) {
      return numbers.length > 0 ? `(${numbers}` : numbers;
    } else if (numbers.length <= 6) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else if (numbers.length <= 10) {
      // Telefone fixo: (XX) XXXX-XXXX
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    } else {
      // Celular: (XX) 9XXXX-XXXX
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  const validatePhone = (phone: string): boolean => {
    // Remove formatação
    const numbers = phone.replace(/\D/g, '');
    
    // Valida formato brasileiro:
    // - Deve ter 10 ou 11 dígitos
    // - DDD (código de área) deve ter 2 dígitos e não pode ser 00
    // - Primeiro dígito após DDD não pode ser 0 ou 1 para celular
    
    if (numbers.length < 10 || numbers.length > 11) {
      setError('Telefone deve ter 10 ou 11 dígitos');
      return false;
    }

    const ddd = numbers.slice(0, 2);
    if (ddd === '00' || parseInt(ddd) < 11 || parseInt(ddd) > 99) {
      setError('DDD inválido');
      return false;
    }

    // Para celular (11 dígitos), o 3º dígito deve ser 9
    if (numbers.length === 11 && numbers[2] !== '9') {
      setError('Celular deve começar com 9 após o DDD');
      return false;
    }

    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1+$/.test(numbers)) {
      setError('Número de telefone inválido');
      return false;
    }

    setError('');
    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    onChange(formatted);
    
    // Valida apenas se o campo tiver 10 ou 11 dígitos
    const digits = formatted.replace(/\D/g, '').length;
    if (digits >= 10) {
      validatePhone(formatted);
    } else {
      setError('');
    }
  };

  const handleBlur = () => {
    if (value && value.replace(/\D/g, '').length > 0) {
      validatePhone(value);
    }
  };

  const inputId = `phone-input-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div style={{ marginBottom: '16px' }}>
      <label htmlFor={inputId} style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
        {label} {required && '*'}
      </label>
      <input
        id={inputId}
        name="phone"
        type="tel"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="(31) 99999-8888"
        autoComplete="tel"
        required={required}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: `1px solid ${error ? '#ef4444' : '#ddd'}`,
          borderRadius: '6px',
          fontSize: '14px',
          outline: 'none',
          transition: 'border-color 0.2s'
        }}
      />
      {error && (
        <span style={{ 
          display: 'block', 
          marginTop: '4px', 
          fontSize: '12px', 
          color: '#ef4444' 
        }}>
          {error}
        </span>
      )}
      {!error && value && value.replace(/\D/g, '').length === 10 && (
        <span style={{ 
          display: 'block', 
          marginTop: '4px', 
          fontSize: '12px', 
          color: '#10b981' 
        }}>
          ✓ Valid phone number
        </span>
      )}
    </div>
  );
};

export default PhoneInput;

